import { api } from "./client";

/** The assistants' tools (decision 0033): the shapes the API answers and takes. */
export type ToolInputType = "text" | "number" | "boolean" | "list" | "date";
export interface ToolInput {
  key: string;
  type: ToolInputType;
  required: boolean;
  description: string;
  options?: string[];
}
export type ToolResponseMode =
  | { mode: "text" }
  | { mode: "field"; field: string }
  | { mode: "template"; template: string };
export type ToolEffect = "read" | "write";
export type ToolConfirm = "contact" | "person" | "none";

/** The form, as it is saved and tested. */
export interface ToolBody {
  key: string;
  name: string;
  description: string;
  connectionId: string | null;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  protocol: "donetalk" | "any";
  inputs: ToolInput[];
  request: { headers: Record<string, string>; body?: string };
  response: ToolResponseMode;
  effect: ToolEffect;
  confirm: ToolConfirm;
  preview: boolean;
  timeoutMs: number;
  maxChars: number;
  hourlyLimit: number;
  contactFields: string[];
  isActive: boolean;
}

/** A tool as the database keeps it. */
export interface ToolRow {
  id: string;
  key: string;
  name: string;
  description: string;
  kind: "form" | "flow";
  connection_id: string | null;
  method: ToolBody["method"];
  path: string;
  protocol: ToolBody["protocol"];
  inputs: ToolInput[];
  request: ToolBody["request"];
  response: ToolResponseMode;
  effect: ToolEffect;
  confirm: ToolConfirm;
  preview: boolean;
  timeout_ms: number;
  max_chars: number;
  hourly_limit: number;
  contact_fields: string[];
  package: string | null;
  is_active: boolean;
}
export interface ToolListRow extends ToolRow {
  uses60d: number;
  failed60d: number;
  assistants: Array<{ id: string; name: string }>;
}

export type AuthKind = "donetalk" | "copiloto" | "bearer" | "header" | "none";
export interface Connection {
  id: string;
  name: string;
  baseUrl: string;
  authKind: AuthKind;
  authHeader: string | null;
  secretHint: string | null;
}
export interface ConnectionInput {
  name: string;
  baseUrl: string;
  authKind: AuthKind;
  authHeader: string | null;
  /** Write-only: absent keeps the stored one. */
  secret?: string;
}

export interface ToolTestResult {
  called: boolean;
  request: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body: string | null;
  };
  response?: { status: number; body: string } | null;
  status: "ok" | "refused" | "failed" | "simulated";
  text: string | null;
  error?: string | null;
  httpStatus: number | null;
  latencyMs: number;
}

export type ToolCallStatus =
  | "ok"
  | "refused"
  | "failed"
  | "limited"
  | "simulated"
  | "awaiting_contact"
  | "awaiting_person"
  | "declined"
  | "expired";
export interface ToolCallRow {
  id: string;
  tool_id: string;
  tool_name: string;
  assistant_id: string | null;
  assistant_name: string | null;
  conversation_id: string | null;
  contact_name: string | null;
  status: ToolCallStatus;
  input: Record<string, unknown>;
  result_text: string | null;
  http_status: number | null;
  latency_ms: number | null;
  error: string | null;
  decided_by: string | null;
  confirmed_at: string | null;
  created_at: string;
}
export interface ToolCallsPage {
  calls: ToolCallRow[];
  more: boolean;
  summary: { total: number; ok: number; failed: number };
  byTool: Array<{ tool_id: string; name: string; n: number; failed: number }>;
}

export interface ToolPackage {
  key: string;
  connection: { name: string; baseUrl: string; authKind: AuthKind };
  tools: Array<{
    key: string;
    name: string;
    effect: ToolEffect;
    confirm: ToolConfirm;
    preview: boolean;
    installed: boolean;
  }>;
}

const base = (slug: string) => `/v1/c/${slug}`;

export const toolsApi = {
  list: (slug: string) =>
    api<{
      tools: ToolListRow[];
      connections: Connection[];
      packages: string[];
    }>(`${base(slug)}/tools`),
  get: (slug: string, id: string) =>
    api<{ tool: ToolRow }>(`${base(slug)}/tools/${id}`),
  create: (slug: string, body: ToolBody) =>
    api<{ tool: { id: string } }>(`${base(slug)}/tools`, {
      method: "POST",
      body,
    }),
  update: (slug: string, id: string, body: ToolBody) =>
    api<{ ok: true }>(`${base(slug)}/tools/${id}`, { method: "PATCH", body }),
  remove: (slug: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/tools/${id}`, { method: "DELETE" }),
  test: (
    slug: string,
    body: {
      tool: ToolBody;
      input: Record<string, unknown>;
      phone?: string | null;
      real: boolean;
    },
  ) =>
    api<ToolTestResult>(`${base(slug)}/tools/test`, { method: "POST", body }),
  draft: (slug: string, body: { description: string; docs?: string }) =>
    api<{ draft: ToolBody }>(`${base(slug)}/tools/draft`, {
      method: "POST",
      body,
    }),
  connections: (slug: string) =>
    api<{ connections: Connection[] }>(`${base(slug)}/tool-connections`),
  createConnection: (slug: string, body: ConnectionInput) =>
    api<{ connection: { id: string } }>(`${base(slug)}/tool-connections`, {
      method: "POST",
      body,
    }),
  updateConnection: (slug: string, id: string, body: ConnectionInput) =>
    api<{ ok: true }>(`${base(slug)}/tool-connections/${id}`, {
      method: "PATCH",
      body,
    }),
  removeConnection: (slug: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/tool-connections/${id}`, {
      method: "DELETE",
    }),
  calls: (
    slug: string,
    q: {
      toolId?: string;
      assistantId?: string;
      status?: string;
      days?: number;
      before?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(q))
      if (v !== undefined && v !== "") params.set(k, String(v));
    const qs = params.toString();
    return api<ToolCallsPage>(`${base(slug)}/tool-calls${qs ? `?${qs}` : ""}`);
  },
  decide: (slug: string, callId: string, approve: boolean) =>
    api<{ ok: true; status: string }>(
      `${base(slug)}/tool-calls/${callId}/decide`,
      { method: "POST", body: { approve } },
    ),
  packages: (slug: string) =>
    api<{ packages: ToolPackage[] }>(`${base(slug)}/tool-packages`),
  testPackage: (
    slug: string,
    key: string,
    body: { secret: string; baseUrl?: string },
  ) =>
    api<{
      status: string;
      httpStatus: number | null;
      latencyMs: number;
      error: string | null;
      tool: string;
    }>(`${base(slug)}/tool-packages/${key}/test`, { method: "POST", body }),
  install: (
    slug: string,
    key: string,
    body: {
      secret: string;
      baseUrl?: string;
      tools: string[];
      assistantIds: string[];
    },
  ) =>
    api<{ connectionId: string; toolIds: string[] }>(
      `${base(slug)}/tool-packages/${key}/install`,
      { method: "POST", body },
    ),
};
